import cx from 'classnames'
import {
  autoUpdate,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react-dom-interactions'
import { IconButton } from 'components/IconButton/IconButton'
import { flatten } from 'lodash'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { ReactNode, useEffect } from 'react'
import { CSSTransition } from 'react-transition-group'
import { useDocStore } from 'stores/DocStore'
import { DocRightRailViewItem } from 'types'
import Styles from './FloatingDocRightRail.module.scss'
import { Tooltip } from 'components/Tooltip/Tooltip'

interface Props {
  children: ReactNode
  anchor?: HTMLDivElement | null
  view: DocRightRailViewItem | DocRightRailViewItem[]
  icon: ReactNode
  label: string
}

export const PanelTrigger = observer(({ children, view, icon, anchor, label }: Props) => {
  const { rightRailStore } = useDocStore()
  const isSelected = computed(() => {
    if (!rightRailStore.activeTab || rightRailStore.locked) {
      return false
    }
    return flatten([view]).includes(rightRailStore.activeTab)
  }).get()

  const { x, y, reference, floating, context } = useFloating({
    open: isSelected,
    placement: 'left-start',
    strategy: 'fixed',
    middleware: [
      offset(4),
      shift({ padding: 4 }),
      size({
        padding: 16,
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
          })
        },
      }),
    ],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })

  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      const shouldClose = !(event.target as HTMLElement)?.closest('main .ProseMirror')
      if (shouldClose) {
        rightRailStore.toggleActiveTab(flatten([view])[0])
      }
      return false
    },
  })
  const { getFloatingProps } = useInteractions([dismiss])

  useEffect(() => {
    if (anchor) {
      reference(anchor)
    }
  }, [anchor, reference])

  const coordsAvailable = x !== null && y !== null

  return (
    <>
      <Tooltip label={label} placement="left">
        <IconButton
          aria-label={label}
          size="xl"
          onPress={() => rightRailStore.toggleActiveTab(flatten([view])[0])}
          className={cx(Styles.trigger, {
            [Styles.selected]: isSelected,
          })}
        >
          {icon}
        </IconButton>
      </Tooltip>

      <CSSTransition
        in={isSelected}
        appear
        unmountOnExit
        addEndListener={(node, done) => {
          node.addEventListener('transitionend', done, false)
        }}
        classNames={{
          enter: Styles.fadeIn,
          enterActive: Styles.fadeInActive,
          exit: Styles.fadeOut,
          exitActive: Styles.fadeOutActive,
        }}
      >
        <div
          className={Styles.panel}
          ref={floating}
          style={{
            visibility: coordsAvailable ? 'visible' : 'hidden',
            position: 'fixed',
            top: y || 0,
            left: x || 0,
          }}
          {...getFloatingProps()}
        >
          {children}
        </div>
      </CSSTransition>
    </>
  )
})
